import { useEffect, useRef } from 'react';
import Container from '../../components/Container';
import { Button, ConfirmModal } from '../../components/SecondaryMarket';
import ViewReverse from '../../components/ViewReverse';
import { fontSizes } from '../../constants/constants';
import BankAndCustodianInformation from './components/BankAndCustodianInformation';
import {
  AccountOperation,
  ClientClassification,
  ContactInformation,
  DigitalAuthorization,
  DocumentUpload,
  FormSteps,
  GeneralInformation,
  InvestmentPortfolio,
  Progress,
  SubmitApplication,
} from './components';
import CorporateFormFunctional from './index.function';
import { formSteps } from '../../config/config';
import { cn } from '../../utils/utils';
import { RejectionModal } from '../CorporateKyc/components';
import icons from '../../constants/icons';
import FullScreenLoader from '../../components/FullScreenLoader';

const CorporateForm = () => {
  const {
    TextWeight,
    getContentKey,
    forms_percentage,
    corporate_kyc_data,
    corporate_save_loading,
    clearFormErrorBorder,
    formErrorBorders,
    generalInformationFormikRef,
    contactInformationFormikRef,
    clientClassificationFormikRef,
    investmentPortfolioFormikRef,
    bankAndCustodianFormikRef,
    accountOperationFormikRef,
    handleSave,
    handleLogout,
    isArabic,
    setRejectionModal,
    rejection_summary,
    rejectionModal,
    isExit,
    setIsExit,
    step,
    setStep,
    isTermsAgreed,
    setIsTermsAgreed,
    unsavedChangesModal,
    setUnsavedChangesModal,
    common_loading,
    saved_form_steps,
  } = CorporateFormFunctional();

  const isSubmit = corporate_kyc_data?.applicationFacade?.applicationProgress === 100;

  const headerRef = useRef(null);

  // Container scrolls internally (not the window), so reset it to top on every tab change
  useEffect(() => {
    let el = headerRef.current?.parentElement;
    while (el) {
      const { overflowY } = window.getComputedStyle(el);
      if (overflowY === 'auto' || overflowY === 'scroll') {
        el.scrollTo({ top: 0 });
        break;
      }
      el = el.parentElement;
    }
    window.scrollTo({ top: 0 });
  }, [step]);

  return (
    <Container className={'px-0 md:px-0 pt-0'} handleLogout={handleLogout} onLogoutPress={() => setUnsavedChangesModal(true)}>
      <div ref={headerRef} className='sticky top-0 z-20 bg-background pb-1'>
        <FormSteps getContentKey={getContentKey} step={step} setStep={setStep} formsPercentage={forms_percentage} />

        <ViewReverse
          className={'bg-white  rounded-xl flex-1 px-4 md:px-8 py-5 mt-6 mx-4 md:mx-12 items-center justify-between gap-4'}
          isResponsive
        >
          <Progress percentage={corporate_kyc_data?.applicationFacade?.applicationProgress} step={step} total={formSteps?.length} getContentKey={getContentKey} />
          <ViewReverse className={'gap-2 w-full justify-end'} isResponsive>
            <Button
              text={getContentKey("save_and_exit")}
              className={'bg-transparent border border-grey text-black'}
              weight={TextWeight.bold}
              textSize={fontSizes.h14}
              isDisabled={corporate_save_loading}
              onClick={() => {
                setIsExit(true)
                handleSave(false)
              }
              }
              loading={corporate_save_loading && isExit}
            />
            <Button
              text={step === 9 ? getContentKey('submit_proceed') : getContentKey("save_and_proceed")}
              weight={TextWeight.bold}
              textSize={fontSizes.h14}
              isDisabled={step == 9 && (!isSubmit || !isTermsAgreed) || corporate_save_loading}
              onClick={() => {
                setIsExit(false)
                handleSave(true)
              }}
              loading={corporate_save_loading && !isExit}
            />
          </ViewReverse>
        </ViewReverse>
      </div>

      <div className={cn('bg-white rounded-xl flex-1 px-4 md:px-8 pt-8 pb-10 mt-6 mx-4 md:mx-12', saved_form_steps?.includes(step) && 'highlight-required')}>
        {step === 1 ? (
          <div
            className={cn('bg-white  rounded-xl px-4 md:px-8 py-6 flex-1 border', formErrorBorders?.generalInformation ? 'border-red-500' : 'border-transparent')}
            onClick={() => clearFormErrorBorder('generalInformation')}
          >
            <GeneralInformation
              contentKey={'corporate_general_information'}
              formRef={generalInformationFormikRef}
              percentage={forms_percentage?.fGeneralInformation?.progress}
            />
          </div>
        ) : step === 2 ? (
          <div
            className={cn('bg-white  rounded-xl px-4 md:px-8 py-6 flex-1 border', formErrorBorders?.contactInformation ? 'border-red-500' : 'border-transparent')}
            onClick={() => clearFormErrorBorder('contactInformation')}
          >
            <ContactInformation
              contentKey={'corporate_contact_information'}
              formRef={contactInformationFormikRef}
              percentage={forms_percentage?.fContactInformation?.progress}
            />
          </div>
        ) : step === 3 ? (
          <div className={cn('bg-white  rounded-xl px-4 md:px-8 py-6 flex-1 border', formErrorBorders?.accountOperation ? 'border-red-500' : 'border-transparent')}
            onClick={() => clearFormErrorBorder('accountOperation')}>
            <AccountOperation
              contentKey={''}
              formRef={accountOperationFormikRef}
              percentage={forms_percentage?.fAccountOperation?.progress}
            />
          </div>
        ) : step === 4 ? (
          <div
            className={cn('bg-white  rounded-xl px-4 md:px-8 py-6 flex-1 border', formErrorBorders?.clientClassification ? 'border-red-500' : 'border-transparent')}
            onClick={() => clearFormErrorBorder('clientClassification')}
          >
            <ClientClassification
              contentKey={'corporate_client_classification'}
              formRef={clientClassificationFormikRef}
              percentage={forms_percentage?.fClientClassification?.progress}
            />
          </div>
        ) : step === 5 ? (
          <div className={cn('bg-white  rounded-xl px-4 md:px-8 py-6 flex-1 border', formErrorBorders?.investmentPortfolio ? 'border-red-500' : 'border-transparent')}
            onClick={() => clearFormErrorBorder('investmentPortfolio')}>
            <InvestmentPortfolio
              contentKey={'corporate_investment_portfolio'}
              formRef={investmentPortfolioFormikRef}
              percentage={forms_percentage?.fInvestmentPortfolio?.progress}
            />
          </div>
        ) : step === 6 ? (
          <div className={cn('bg-white  rounded-xl px-4 md:px-8 py-6 flex-1 border', formErrorBorders?.bankAndCustodian ? 'border-red-500' : 'border-transparent')}
            onClick={() => clearFormErrorBorder('bankAndCustodian')}>
            <BankAndCustodianInformation
              contentKey={'corporate_bank_custodian'}
              formRef={bankAndCustodianFormikRef}
              percentage={forms_percentage?.fBankAndCustodianInformation?.progress}
            />
          </div>
        )
          : step === 7 ? (
            <DigitalAuthorization
              contentKey={'corporate_digital_authorization'}
            />
          )
            : step === 8 ? (
              <DocumentUpload
                contentKey={'corporate_document_vault'}
                percentage={forms_percentage?.fDocumentVault?.progress}
              />
            ) : step === 9 ? (
              <SubmitApplication
                contentKey={'corporate_review_submit'}
                isTermsAgreed={isTermsAgreed}
                setIsTermsAgreed={setIsTermsAgreed}
                setStep={setStep}
              />
            ) : null}
      </div>

      <RejectionModal
        TextWeight={TextWeight}
        setRejectionModal={setRejectionModal}
        rejectionModal={rejectionModal}
        rejection_summary={rejection_summary}
        getContentKey={getContentKey}
        logout={handleLogout}
        isAccountOpening={true}
        isArabic={isArabic}
      />

      <ConfirmModal
        isOpen={unsavedChangesModal}
        onClose={() => setUnsavedChangesModal(false)}
        icon={icons.infologout}
        title={getContentKey('unsaved_changes_detected')}
        descOne={getContentKey('unsaved_changes_desc_one')}
        descTwo={getContentKey('unsaved_changes_desc_two')}
        leftBtn={getContentKey('discard_changes')}
        onLeftPress={handleLogout}
        rightBtn={getContentKey('save_and_exit')}
        onRightPress={() => {
          setUnsavedChangesModal(false)
          setIsExit(true)
          handleSave(false)
        }}
      />

      {common_loading && <FullScreenLoader />}
    </Container>
  );
};

export default CorporateForm;
